import type { DriverAvailabilityHistoryEntry, DriverAvailabilityStatus } from '@chukua/common';
import { Table, Tag, type TableColumnsType } from 'antd';
import { History, ShieldAlert } from 'lucide-react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { useAvailabilityHistory } from '../hooks/use-availability-history';
import { AVAILABILITY_COLORS, AVAILABILITY_LABELS } from '../policies/availability-labels';

const REASON_LABELS: Record<string, string> = {
  created: 'Driver created',
  backfilled: 'Recorded when availability was introduced',
  trip_assigned: 'Assigned to a trip',
  trip_closed_out: 'Trip closed out',
  employment_changed: 'Employment status changed',
  rest_elapsed: 'Rest period over',
};

function StatusTag({ status }: { status: DriverAvailabilityStatus }) {
  return <Tag color={AVAILABILITY_COLORS[status]}>{AVAILABILITY_LABELS[status]}</Tag>;
}

const COLUMNS: TableColumnsType<DriverAvailabilityHistoryEntry> = [
  { title: 'When', key: 'occurredAt', render: (_, entry) => new Date(entry.occurredAt).toLocaleString() },
  {
    title: 'From',
    key: 'fromStatus',
    render: (_, entry) => (entry.fromStatus ? <StatusTag status={entry.fromStatus} /> : <span className="text-muted">—</span>),
  },
  { title: 'To', key: 'toStatus', render: (_, entry) => <StatusTag status={entry.toStatus} /> },
  { title: 'Why', key: 'reason', render: (_, entry) => REASON_LABELS[entry.reason] ?? entry.reason },
];

/** Every change of availability, newest first: the evidence behind the status on the Details tab. */
export function DriverAvailabilityHistory({ driverId }: { driverId: string }) {
  const { state, list } = useAvailabilityHistory(driverId);

  if (state.status === 'loading') return <TableSkeleton label="Loading availability history" />;
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load availability history" description="Something went wrong. Try reloading the page." />;
  }
  if (state.page.total === 0) {
    return (
      <EmptyState
        icon={History}
        title="No availability changes yet"
        description="Changes appear here as the driver is assigned to trips and trips close out."
      />
    );
  }

  return (
    <section aria-label="Availability history" className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-fg">Availability history</h3>
      <Table
        rowKey="id"
        dataSource={state.page.items}
        columns={COLUMNS}
        scroll={{ x: 'max-content' }}
        {...list.tableProps<DriverAvailabilityHistoryEntry>(state.page.total)}
      />
    </section>
  );
}
